import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { SeoHead } from './SeoHead'

import { usePropertyProvider } from '../../../hooks'
import {
  combineSchemas,
  createBreadcrumbSchema,
  createHotelSchema,
  ensureAbsoluteUrl,
  getPropertyOgImage,
} from '../../../utils'

export const PropertyDetailHead: React.FC = () => {
  const location = useLocation()
  const { i18n } = useTranslation()
  const propertyData = usePropertyProvider()

  // Get localized content
  const currentLocale = i18n.language
  const isJapanese = currentLocale === 'ja'
  const publication =
    propertyData?.publications?.find((pub) => pub.locale === currentLocale) ||
    propertyData?.publications?.[0]

  // Match the existing UI: prefer the publication name, falling back to the
  // building name. Requiring `name` alone would drop the Head for properties
  // that only expose a name via publications[].name, so render as long as
  // either source provides a name.
  const propertyName = publication?.name || propertyData?.name || ''
  if (!propertyData || !propertyName) {
    return null
  }

  const baseUrl = 'https://booking.hkdballpark.com'
  const currentUrl = `${baseUrl}${location.pathname}`

  const propertyDescription = publication?.description || ''

  // Generate dynamic OG image
  const ogImage = ensureAbsoluteUrl(
    getPropertyOgImage({
      propertyId: propertyData.id?.toString() || '',
      propertyName,
      images:
        propertyData.images?.map((img) => img.url || '').filter(Boolean) || [],
    }),
    baseUrl
  )

  // Create breadcrumb data.
  // 構造化データ上のパンくず名も表示言語に合わせて切り替える (最低限 ja/en)。
  const breadcrumbs = [
    { name: isJapanese ? 'ホーム' : 'Home', url: baseUrl },
    { name: isJapanese ? '施設一覧' : 'Property List', url: `${baseUrl}/list` },
    { name: propertyName, url: currentUrl },
  ]

  // Determine property type for structured data
  let propertyType = 'Hotel'
  if (propertyData.isSpa) {
    propertyType = 'HealthAndBeautyBusiness'
  } else if (propertyData.isActivity) {
    propertyType = 'SportsActivityLocation'
  }

  // Create hotel/business schema
  const propertySchema =
    propertyType === 'Hotel'
      ? createHotelSchema({
          name: propertyName,
          description: propertyDescription,
          image: propertyData.images
            ?.map((img) => ensureAbsoluteUrl(img.url || '', baseUrl))
            .filter(Boolean),
          url: currentUrl,
          priceRange: '¥¥',
        })
      : {
          '@context': 'https://schema.org',
          '@type': propertyType,
          name: propertyName,
          description: propertyDescription,
          image: propertyData.images
            ?.map((img) => ensureAbsoluteUrl(img.url || '', baseUrl))
            .filter(Boolean),
          url: currentUrl,
        }

  // Combine all structured data
  const structuredData = combineSchemas(
    createBreadcrumbSchema(breadcrumbs),
    propertySchema
  )

  // SEO title and description
  const seoTitle = propertyData.insideFvillage
    ? `${propertyName} - Fビレッジ内｜北海道ボールパーク 宿泊予約`
    : `${propertyName} - 北海道ボールパークFビレッジ 宿泊予約`

  const seoDescription =
    propertyDescription ||
    `${propertyName}の宿泊予約。北海道ボールパークFビレッジ${
      propertyData.insideFvillage ? '内' : '近郊'
    }の宿泊施設。`

  return (
    <SeoHead
      title={seoTitle}
      description={seoDescription}
      canonical={currentUrl}
      ogTitle={propertyName}
      ogDescription={propertyDescription}
      ogImage={ogImage}
      ogImageAlt={
        isJapanese ? `${propertyName}の外観` : `Exterior of ${propertyName}`
      }
      ogImageWidth="1200"
      ogImageHeight="630"
      ogType={propertyData.isHotel ? 'hotel' : 'website'}
      ogUrl={currentUrl}
      twitterCard="summary_large_image"
      twitterSite="@ballpark_htb"
      structuredData={structuredData}
      alternateLanguages={[
        { lang: 'ja', href: currentUrl },
        { lang: 'en', href: `${currentUrl}?lang=en` },
      ]}
    />
  )
}
